import { ref, watch, type Ref } from 'vue';
import { CallDirection, type CallLogQuery, type HomeServiceOrderType } from '../../../../gql/graphql';

export const useConfirmationCall = (
  order: Ref<(Pick<HomeServiceOrderType, 'id' | 'callerId' | 'callLogId'> & { caller?: { phoneNumber?: string | null } | null }) | null>,
  currentCall: Ref<Pick<
    NonNullable<CallLogQuery['callLog']>,
    'id' | 'callerId' | 'direction' | 'fromPhoneNumber' | 'toPhoneNumber' | 'resolvedPhoneNumber'
  > | null>,
  reportCall: typeof currentCall,
) => {
  const callLogId = ref<number>();
  let orderId: number | undefined;
  // Keep call context if hangup or report dismissal happens while the order is loading.
  let observedCall = currentCall.value || reportCall.value;

  watch(
    [order, currentCall, reportCall],
    ([value, call, report]) => {
      if (value?.id !== orderId) {
        orderId = value?.id;
        callLogId.value = undefined;
        if (!value) {
          observedCall = call || report;
        }
      }
      observedCall ||= call || report;

      if (!value) {
        return;
      }

      for (const candidate of [call, observedCall, report]) {
        if (!candidate) {
          continue;
        }
        const phoneNumber =
          candidate.direction === CallDirection.Outbound
            ? candidate.toPhoneNumber
            : candidate.resolvedPhoneNumber || candidate.fromPhoneNumber;
        const matchesCaller = candidate.callerId
          ? candidate.callerId === value.callerId
          : !!value.caller?.phoneNumber && phoneNumber === value.caller.phoneNumber;
        if (matchesCaller) {
          observedCall = candidate;
          callLogId.value = candidate.id;
          break;
        }
      }
    },
    { immediate: true, flush: 'sync' },
  );

  return (isStatusChangingToScheduled: boolean) => {
    if (isStatusChangingToScheduled && callLogId.value) {
      return callLogId.value;
    }
    return order.value?.callLogId || callLogId.value;
  };
};
